/**
 * ArticleShareRow — distribution-ready share intent buttons on every
 * published Learn article. Deep-links to native share composer for:
 *   Pinterest, X (Twitter), LinkedIn, Facebook, WhatsApp, Email, Copy link.
 *
 * Built so the Phase C-9 social variants the admin generated (in
 * /admin → Authority → Distribution → Social) can later be wired up
 * as default share text. For now, falls back to article.meta_description.
 */
import { useState } from "react";
import { Pin, Twitter, Linkedin, Facebook, MessageCircle, Mail, Link as LinkIcon, Check } from "lucide-react";
import { PROD_DOMAIN } from "../../lib/seo";


export default function ArticleShareRow({ article, pinImage }) {
  const url = `${PROD_DOMAIN}/learn/${article.slug}`;
  const text = article.meta_description || article.hero_excerpt || article.title || "";
  const u = encodeURIComponent;

  const links = [
    {
      key: "pinterest",
      label: "Pinterest",
      icon: Pin,
      href: pinImage
        ? `https://www.pinterest.com/pin/create/button/?url=${u(url)}&media=${u(pinImage)}&description=${u(text)}`
        : `https://www.pinterest.com/pin/create/button/?url=${u(url)}&description=${u(text)}`,
    },
    {
      key: "x",
      label: "X",
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?url=${u(url)}&text=${u(text)}`,
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${u(url)}`,
    },
    {
      key: "facebook",
      label: "Facebook",
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${u(url)}`,
    },
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: MessageCircle,
      href: `https://wa.me/?text=${u(text + " " + url)}`,
    },
    {
      key: "email",
      label: "Email",
      icon: Mail,
      href: `mailto:?subject=${u(article.title || "Worth a read on Feel Greats")}&body=${u(text + "\n\n" + url)}`,
    },
  ];

  return (
    <div
      className="mt-12 rounded-3xl border border-emerald-100 bg-emerald-50/30 p-5 sm:p-6"
      data-testid="learn-article-share-row"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
            Found this useful?
          </p>
          <p className="font-display text-base sm:text-lg text-emerald-950 mt-1 font-light">
            Save it · share it · pin it for later.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {links.map((l) => {
            const Icon = l.icon;
            return (
              <a
                key={l.key}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-emerald-100 hover:border-emerald-200 hover:bg-emerald-50 text-emerald-900 px-3 py-1.5 text-[12px] font-semibold transition-colors"
                title={`Share to ${l.label}`}
                data-testid={`learn-article-share-${l.key}`}
              >
                <Icon className="w-3.5 h-3.5" /> {l.label}
              </a>
            );
          })}
          <CopyLinkButton url={url} />
        </div>
      </div>
    </div>
  );
}


function CopyLinkButton({ url }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // best-effort fallback: select-then-copy in a hidden input
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) { /* ignored */ }
      document.body.removeChild(ta);
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="inline-flex items-center gap-1.5 rounded-full bg-white border border-emerald-100 hover:border-emerald-200 hover:bg-emerald-50 text-emerald-900 px-3 py-1.5 text-[12px] font-semibold transition-colors"
      data-testid="learn-article-share-copy"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-700" /> : <LinkIcon className="w-3.5 h-3.5" />}
      {copied ? "Copied" : "Copy link"}
    </button>
  );
}
